// src/lib/api.js
import { get } from "svelte/store";
import { accessToken, user } from "$lib/stores/store";

const LOCAL_API_ORIGINS = [
    "http://localhost:4000",
    "https://localhost:4443"
];

function isLocalHostname(hostname) {
    return ["localhost", "127.0.0.1", "0.0.0.0"].includes(String(hostname || "").toLowerCase());
}

function buildRemoteApiCandidates() {
    if (typeof location === "undefined") {
        return [...LOCAL_API_ORIGINS];
    }

    const currentOrigin = `${location.protocol}//${location.host}`;
    const currentIsLocal = isLocalHostname(location.hostname);
    const preferred = location.protocol === "https:" ? ["https://localhost:4443", "http://localhost:4000"] : ["http://localhost:4000", "https://localhost:4443"];
    const candidates = currentIsLocal ? [currentOrigin, ...preferred] : preferred;

    return [...new Set(candidates)];
}

function resolveInitialBaseURL(explicitBaseURL = "") {
    if (typeof location === "undefined") {
        if (import.meta.env.VITE_API_BASE_URL) {
            return String(import.meta.env.VITE_API_BASE_URL).replace(/\/$/, "");
        }

        if (explicitBaseURL) {
            return String(explicitBaseURL).replace(/\/$/, "");
        }

        return "";
    }

    const currentOrigin = `${location.protocol}//${location.host}`;
    const envBaseURL = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
    const fallbackBaseURL = String(explicitBaseURL || "").replace(/\/$/, "");

    if (envBaseURL) {
        try {
            const resolvedEnv = new URL(envBaseURL);
            const envHost = String(resolvedEnv.hostname || "").toLowerCase();
            const currentHost = String(location.hostname || "").toLowerCase();
            const isLocalEnv = isLocalHostname(envHost);
            const isLocalCurrent = isLocalHostname(currentHost);

            if (!(isLocalEnv && !isLocalCurrent)) {
                return envBaseURL;
            }
        } catch {
            return envBaseURL;
        }
    }

    if (fallbackBaseURL) {
        return fallbackBaseURL;
    }

    if (!isLocalHostname(location.hostname)) {
        return currentOrigin;
    }

    return buildRemoteApiCandidates()[0] || currentOrigin;
}

function getRemoteApiPort(value, fallback = 4041) {
    const rawPort = String(value ?? "").trim() || String(fallback);
    const port = Number.parseInt(rawPort, 10);
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
        throw new Error("ApiRemote port must be an integer between 1 and 65535");
    }
    return port;
}

function getRemoteApiBaseURL({ host, port, protocol } = {}) {
    const normalizedHost = String(host || import.meta.env.VITE_REMOTE_API_HOST || "localhost")
        .trim()
        .replace(/^https?:\/\//i, "")
        .replace(/\/+$/, "");
    const normalizedProtocol = String(protocol || import.meta.env.VITE_REMOTE_API_PROTOCOL || "http")
        .trim()
        .replace(/:$/, "")
        .toLowerCase();

    if (!normalizedHost || normalizedHost.includes("/")) {
        throw new Error("ApiRemote host must be a hostname without protocol or path");
    }
    if (normalizedProtocol !== "http" && normalizedProtocol !== "https") {
        throw new Error("ApiRemote protocol must be http or https");
    }

    return `${normalizedProtocol}://${normalizedHost}:${getRemoteApiPort(port ?? import.meta.env.VITE_REMOTE_API_PORT, 4041)}`;
}

/**
 * ApiClient
 * - Auto attach Authorization header
 * - Auto refresh access token when receive 401
 * - Queue requests while refresh in progress
 * - Retry original request once
 * - Tu dong fallback local API origin giua http://localhost:4000 va https://localhost:4443
 */
export default class ApiClient {
    constructor({
        baseURL = "",
        withCredentials = true,
        defaultHeaders = { "Content-Type": "application/json" },
        defaultResponseType = "json",
        forceBaseURL = false,
        localFallback = true
    } = {}) {
        this.baseURL = forceBaseURL
            ? String(baseURL || "").replace(/\/$/, "")
            : resolveInitialBaseURL(baseURL);
        this.withCredentials = withCredentials;
        this.defaultHeaders = defaultHeaders;
        this.defaultResponseType = defaultResponseType;
        this.localFallback = localFallback;

        this.latestAccessToken = null;

        this._isRefreshing = false;
        this._queue = [];
        this._localBaseCandidates = buildRemoteApiCandidates();
    }

    setAccessToken(token) {
        if (token) {
            accessToken.set(token);
            this.latestAccessToken = token;
        } else {
            accessToken.set("");
            this.latestAccessToken = null;
        }
    }

    _supportsLocalFallback() {
        return this.localFallback && !import.meta.env.VITE_API_BASE_URL && this._localBaseCandidates.length > 0;
    }

    _candidateBaseURLs() {
        const normalizedCurrent = String(this.baseURL || "").replace(/\/$/, "");
        const candidates = [normalizedCurrent, ...this._localBaseCandidates].filter(Boolean);
        return [...new Set(candidates)];
    }

    async _refreshToken() {
        if (this._isRefreshing) {
            return new Promise((resolve, reject) => {
                this._queue.push({ resolve, reject });
            });
        }

        this._isRefreshing = true;

        try {
            const res = await fetch(`${this.baseURL}/auth/refresh`, {
                method: "POST",
                credentials: "include"
            });

            if (!res.ok) throw new Error("Refresh failed");

            const data = await res.json();
            if (!data?.accessToken) throw new Error("No accessToken");

            this.setAccessToken(data.accessToken);

            this._queue.forEach(p => p.resolve(data.accessToken));
            this._queue = [];

            return data.accessToken;
        } catch (err) {
            this._queue.forEach(p => p.reject(err));
            this._queue = [];

            this.setAccessToken("");
            user.set(null);

            throw err;
        } finally {
            this._isRefreshing = false;
        }
    }

    async _doFetch(baseURL, endpoint, options) {
        const url = `${baseURL}${endpoint}`;
        const res = await fetch(url, options);
        return { res, url };
    }

    async _fetchWithLocalFallback(endpoint, options) {
        const candidates = this._candidateBaseURLs();
        let lastError = null;

        for (const candidate of candidates) {
            try {
                const result = await this._doFetch(candidate, endpoint, options);
                if (candidate !== this.baseURL) {
                    this.baseURL = candidate;
                }
                return result;
            } catch (error) {
                lastError = error;
            }
        }

        throw lastError || new Error("Network request failed");
    }

    async request(endpoint, {
        method = "GET",
        data = null,
        headers = {},
        responseType = this.defaultResponseType,
        retry = true
    } = {}) {

        let token = this.latestAccessToken ?? get(accessToken);

        if (!token) {
            try {
                token = await this._refreshToken();
            } catch { }
        }

        const isRawBody =
            data instanceof Blob ||
            data instanceof ArrayBuffer ||
            ArrayBuffer.isView(data);

        const finalHeaders = {
            ...(data instanceof FormData || isRawBody ? {} : this.defaultHeaders),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...headers
        };

        const options = {
            method,
            credentials: this.withCredentials ? "include" : "same-origin",
            headers: finalHeaders
        };

        if (data) {
            options.body = data instanceof FormData || isRawBody
                ? data
                : JSON.stringify(data);
        }

        let fetchResult;

        try {
            fetchResult = this._supportsLocalFallback()
                ? await this._fetchWithLocalFallback(endpoint, options)
                : await this._doFetch(this.baseURL, endpoint, options);
        } catch (error) {
            throw error;
        }

        const { res } = fetchResult;

        if (res.status === 401 && retry) {
            const newToken = await this._refreshToken();

            return this.request(endpoint, {
                method,
                data,
                headers: {
                    ...headers,
                    Authorization: `Bearer ${newToken}`
                },
                responseType,
                retry: false
            });
        }

        if (!res.ok) {
            throw await this._createHTTPError(res);
        }

        return this._parse(res, responseType);
    }

    async _createHTTPError(res) {
        let message = `HTTP ${res.status}`;
        let payload = null;

        try {
            const contentType = String(res.headers.get("content-type") || "").toLowerCase();
            if (contentType.includes("application/json")) {
                payload = await res.json();
                message = payload?.message || payload?.error || message;
            } else {
                const text = (await res.text()).trim();
                if (text) message = text;
            }
        } catch {
            // Keep default fallback message.
        }

        const error = new Error(message);
        error.status = res.status;
        error.response = payload;
        return error;
    }

    _parse(res, type) {
        switch (type) {
            case "text": return res.text();
            case "blob": return res.blob();
            case "arrayBuffer": return res.arrayBuffer();
            case "formData": return res.formData();
            default: return res.json();
        }
    }

    get(url, opts = {}) {
        return this.request(url, { ...opts, method: "GET" });
    }

    post(url, data, opts = {}) {
        return this.request(url, { ...opts, method: "POST", data });
    }

    put(url, data, opts = {}) {
        return this.request(url, { ...opts, method: "PUT", data });
    }

    delete(url, opts = {}) {
        return this.request(url, { ...opts, method: "DELETE" });
    }
}

/**
 * Client dành cho một local API độc lập. Không fallback sang API chính.
 *
 * new ApiRemote()
 * new ApiRemote({ host: 'localhost', port: 5042 })
 * new ApiRemote('localhost', 5042)
 */
export class ApiRemote extends ApiClient {
    constructor(options = {}, positionalPort) {
        const config = typeof options === "string"
            ? { host: options, port: positionalPort }
            : (options || {});
        const {
            host,
            port,
            protocol,
            ...clientOptions
        } = config;

        super({
            ...clientOptions,
            baseURL: getRemoteApiBaseURL({ host, port, protocol }),
            forceBaseURL: true,
            localFallback: false
        });
    }
}

export const api = new ApiClient();
export const apiRemote = new ApiRemote();

export async function postImage(blob) {
    const formData = new FormData();
    formData.append("image", blob, "capture.jpg");
    return api.post("/capture", formData);
}

export async function streamInput(chunk, isLast = false) {
    const formData = new FormData();
    formData.append("chunk", chunk, isLast ? "final.webm" : "chunk.webm");
    formData.append("stop", isLast.toString());
    return api.post("/stream-input", formData);
}

//ok
